import { useTheme } from "@mui/material/styles";
import { QUERY_PROGRESS_SUCCEED } from "core/consts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { useIntParams } from "core/model/utils/urls";
import {
  Auction,
  Patient,
  QueryProgress,
  RehabFormName,
  RehabForms,
} from "core/types";
import ConfirmationDialog from "ds_legacy/components/ConfirmationDialog";
import LinkV2 from "ds_legacy/components/LinkV2";
import RSButton from "ds_legacy/components/RSButton";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  FONT_SIZE_16,
  Title,
} from "ds_legacy/materials/typography";
import { useCareseekerPatientHomeUrls } from "dsl/hooks/useNavigationHandlers";
import { ArrowLeftIcon } from "lucide-react";
import { ReactNode } from "react";
import { FormWatcher } from "react-forms-state";
import { useTranslations } from "translations";
import { useImportFormData } from "../utils";
import { FormDownload } from "./DownloadSectionReha";
import { getRehabFormConfig } from "./configs";
import { RehabFormDataMapped, useDeleteConfirmation } from "./utils";

export const SAVE_AND_OVERWRITE_PDF_ID = "save-and-overwrite-pdf";
export const SAVE_AND_CREATE_PDF_ID = "save-and-create-pdf";
export const IMPORT_AUCTION_ID = "import-auction";
export const REIMPORT_AUCTION_ID = "reimport-auction";

const BackToOverviewLink = () => {
  const theme = useTheme();
  const translations = useTranslations();
  const { goToRehabForms } = useCareseekerPatientHomeUrls();
  const { auctionId, patientId } = useIntParams();

  if (!auctionId || !patientId) return null;

  return (
    <LinkV2
      color="default"
      data-testid="back-link"
      id="back"
      sx={{
        color: theme.palette.primary.main,
        cursor: "pointer",
        padding: padding(3, 0),
      }}
      to={goToRehabForms({
        auctionId,
        patientId,
      })}
    >
      <HorizontalLayout aligned as="span" overflow="visible">
        <ArrowLeftIcon
          style={{ fontSize: FONT_SIZE_16, margin: margin(0, 1, 0, 0) }}
        />
        {translations.providersearch.providerDetailsPage.backToListing}
      </HorizontalLayout>
    </LinkV2>
  );
};

const RehabFormButtons = ({
  deleteFormProgress,
  hasPreviousFormValue,
  importFormData,
  importProgress,
  isFormFilled,
  onDelete,
  onSubmitProgress,
  submit,
  withDelete,
}: {
  deleteFormProgress: QueryProgress;
  hasPreviousFormValue: boolean;
  importFormData: () => void;
  importProgress: QueryProgress;
  isFormFilled?: boolean;
  onDelete: (
    formValue?: RehabFormDataMapped<RehabFormName>,
  ) => Promise<null | undefined>;
  onSubmitProgress: QueryProgress;
  submit: () => void;
  withDelete?: boolean;
}) => {
  const translations = useTranslations();

  return (
    <HorizontalLayout
      justify={withDelete ? "space-between" : "flex-end"}
      margin={margin(0, 0, 1)}
    >
      {withDelete && (
        <FormWatcher watchPath="">
          {({ watchedValue }) => {
            return (
              <RSButton
                color="error"
                id="delete-form"
                loading={deleteFormProgress}
                type="submit"
                variant="outlined"
                onClick={() => onDelete(watchedValue)}
              >
                {translations.patientForms.transitionalCareForm.deleteForm}
              </RSButton>
            );
          }}
        </FormWatcher>
      )}
      <HorizontalLayout justify="flex-end">
        <RSButton
          color="primary"
          id={
            hasPreviousFormValue
              ? SAVE_AND_OVERWRITE_PDF_ID
              : SAVE_AND_CREATE_PDF_ID
          }
          loading={onSubmitProgress}
          type="submit"
          variant="outlined"
          onClick={submit}
        >
          {hasPreviousFormValue
            ? translations.patientForms.buttonSaveOverwrite
            : translations.patientForms.buttonSave}
        </RSButton>
        <RSButton
          color="primary"
          id={isFormFilled ? REIMPORT_AUCTION_ID : IMPORT_AUCTION_ID}
          loading={importProgress}
          type="submit"
          variant={isFormFilled ? "outlined" : "contained"}
          onClick={importFormData}
        >
          {isFormFilled
            ? translations.patientForms.buttonRefreshData
            : translations.patientForms.buttonImport}
        </RSButton>
      </HorizontalLayout>
    </HorizontalLayout>
  );
};

export const RehabFormWrapper = ({
  auction,
  children,
  formName,
  formOnChange,
  hasPreviousFormValue,
  onSubmitProgress,
  patientId,
  rehabForms,
  submit,
  subtitle,
  title,
}: {
  auction: Auction;
  children: ReactNode;
  formName: RehabFormName;
  formOnChange: (value: any, statePath: string, validation: any) => void;
  hasPreviousFormValue: boolean;
  onSubmitProgress: QueryProgress;
  patientId: Patient["id"];
  rehabForms: RehabForms | undefined;
  submit: () => void;
  subtitle: string;
  title: string;
}) => {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const { confirmProps, deleteFormProgress, onDelete } = useDeleteConfirmation({
    auction,
    formName,
    formOnChange,
    patientId,
  });

  const { formConfig } = getRehabFormConfig(formName);

  const { importFormData, importProgress } = useImportFormData({
    auction,
    formGetters: formConfig.importAuctionToFormGetters(
      getOntology,
      translations,
    ),
    formOnChange,
  });

  const isFormFilled =
    hasPreviousFormValue || importProgress === QUERY_PROGRESS_SUCCEED;

  return (
    <>
      <VerticalLayout width="100%">
        <BackToOverviewLink />
        <FormDownload
          careseekerName={auction.patient.careseeker?.name ?? ""}
          externalId={auction.patient.external_id ?? ""}
          formName={formName}
          rehabForms={rehabForms}
          userId={auction.patient.user_id ?? ""}
        />
        <Title as="h3" bold margin={margin(0, 0, 1)}>
          {title}
        </Title>
        <Body
          as="p"
          fontSize={FONT_SIZE_14}
          margin={margin(0, 0, 3)}
          maxWidth="100%"
          width="100%"
        >
          {subtitle}
        </Body>
        <RehabFormButtons
          deleteFormProgress={deleteFormProgress}
          hasPreviousFormValue={hasPreviousFormValue}
          importFormData={importFormData}
          importProgress={importProgress}
          isFormFilled={isFormFilled}
          submit={submit}
          onDelete={onDelete}
          onSubmitProgress={onSubmitProgress}
        />
        {children}
        <RehabFormButtons
          deleteFormProgress={deleteFormProgress}
          hasPreviousFormValue={hasPreviousFormValue}
          importFormData={importFormData}
          importProgress={importProgress}
          isFormFilled={isFormFilled}
          submit={submit}
          withDelete
          onDelete={onDelete}
          onSubmitProgress={onSubmitProgress}
        />
      </VerticalLayout>
      <ConfirmationDialog {...confirmProps} />
    </>
  );
};
